import { faEye, faFile } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TableDataState } from './table-paginated-v8';

export type RowClickedFunctionV8 = (data: any) => void;
export function EyeColumnV8(rowClicked: RowClickedFunctionV8, id = 'eye') {
  return {
    // ΚΟΛΩΝΑ ΜΕ RedEye και Click handler
    id,
    accessorKey: id,
    columnName: id.charAt(0).toUpperCase() + id.slice(1),
    header: () => <FontAwesomeIcon icon={faFile} />,
    cell: ({ row, table }) => (
      <div
        className="text-center"
        style={{ cursor: 'pointer' }}
        onClick={() => {
          if (table.options.meta.dataState === TableDataState.browse) {
            rowClicked(row.original);
          }
        }}
      >
        <FontAwesomeIcon icon={faEye} />
      </div>
    ),
    size: 4,
  };
}

export function SelectColumnV8(headerTitle?: string): {
  id: string;
  header: any;
  cell: any;
  size: number;
} {
  return {
    id: 'select-col',
    header: headerTitle
      ? headerTitle
      : ({ table }) => (
          <div className="text-center">
            <input
              type="checkbox"
              checked={table.getIsAllRowsSelected()}
              // indeterminate={table.getIsSomeRowsSelected()}
              onChange={table.getToggleAllRowsSelectedHandler()} //or getToggleAllPageRowsSelectedHandler
            />
          </div>
        ),
    cell: ({ row }) => (
      <div className="text-center">
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      </div>
    ),
    size: 50,
  };
}

export const CellYesNoV8 = (info) => {
  //const { t } = useTranslation();
  const value = info.getValue();
  return (
    <div className="text-center">
      {value === null ? (
        <div className="text-center font-weight-bold text-primary">{'-'}</div>
      ) : value ? (
        <div className="text-center text-success font-weight-bold">{'NAI'}</div>
      ) : (
        <div className="text-center text-danger font-weight-bold">{'OXI'}</div>
      )}
    </div>
  );
};

export const CellYesNoEngV8 = (info) => {
  const value = info.getValue();
  return (
    <div className="text-center">
      {value === null ? (
        <div className="text-center font-weight-bold text-primary">{'-'}</div>
      ) : value ? (
        <div className="text-center text-success font-weight-bold">{'YES'}</div>
      ) : (
        <div className="text-center text-danger font-weight-bold">{'NO'}</div>
      )}
    </div>
  );
};

export const CellYesNoEmptyV8 = (info) => {
  const { t } = useTranslation();
  const value = info.getValue();
  return (
    <div className="text-center">
      {value === null ? (
        <div className="text-center font-weight-bold text-primary">{'-'}</div>
      ) : value ? (
        <div className="text-center text-success font-weight-bold">{t('NAI')}</div>
      ) : (
        <div className="text-center text-danger font-weight-bold">{t('OXI')}</div>
      )}
    </div>
  );
};

// Reverse Value
export const CellReverseYesNoV8 = (info) => {
  // const { t } = useTranslation();
  const value = info.getValue();

  return (
    <div className="text-center">
      {!value ? (
        <div className="text-center text-success font-weight-bold">{'ΝΑΙ'}</div>
      ) : (
        <div className="text-center text-danger font-weight-bold">{'ΟΧΙ'}</div>
      )}
    </div>
  );
};

// Reverse Colors
export const CellYesNoReverseColorsV8 = (info) => {
  const { t } = useTranslation();
  const value = info.getValue();
  return (
    <div className="text-center">
      {value ? (
        <div className="text-center text-danger font-weight-bold">{t('ΝΑΙ')}</div>
      ) : (
        <div className="text-center text-success font-weight-bold">{t('ΟΧΙ')}</div>
      )}
    </div>
  );
};

/* -------------- Input Box ---------------- */
export const CellInputV8 = (info) => {
  const {
    getValue,
    // row: { index },
    row,
    column: { id },
    table,
  } = info;

  const initialValue = getValue();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = React.useState(initialValue);

  // When the input is blurred, we'll call our table meta's updateData function
  const onBlur = () => {
    if (value !== initialValue) {
      void table.options.meta?.updateData(row.index, id, value, row.original);
    }
  };

  // If the initialValue is changed external, sync it up with our state
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <input
      className="table-cell-input form-control text-center"
      value={value as string}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
    />
  );
};

/* -------------- Input Box ---------------- */
export const CellNumericInputV8 = (info) => {
  const {
    getValue,
    // row: { index },
    row,
    column: { id },
    table,
  } = info;

  const initialValue = getValue();
  // We need to keep and update the state of the cell normally
  const [value, setValue] = React.useState(initialValue);

  // When the input is blurred, we'll call our table meta's updateData function
  const onBlur = () => {
    if (value !== initialValue) {
      void table.options.meta?.updateData(row.index, id, parseInt(value), row.original);
    }
  };

  // If the initialValue is changed external, sync it up with our state
  React.useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  return (
    <input
      className="table-cell-input form-control text-center"
      value={value as string}
      onChange={(e) => setValue(e.target.value)}
      onBlur={onBlur}
    />
  );
};

export const CellCenterV8 = ({ getValue }) => {
  const value = getValue();
  return (
    <div className="text-center">
      {value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : value}
    </div>
  );
};

export const CellLeftV8 = ({ getValue }) => {
  const value = getValue();
  return (
    <div className="text-start">
      {value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : value}
    </div>
  );
};

export const CellRightV8 = ({ getValue }) => {
  const value = getValue();
  return (
    <div className="text-end">
      {value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : value}
    </div>
  );
};

export function CellIntCustomV8(numberWidth: number) {
  return ({ getValue }) => {
    let txt = '-';
    const value = getValue();
    if (typeof value === 'string') {
      const i = parseInt(value, 10);
      if (isNaN(i)) txt = value;
      else txt = i.toLocaleString();
    } else if (typeof value === 'number') {
      txt = value.toLocaleString();
    }
    // pad to 10 chars FIGURE SPACE = &numsp; U+2007
    txt = '\u2007'.repeat(numberWidth - txt.length) + txt;
    return <div className="text-center">{txt}</div>;
  };
}

export function CellFloatCustomV8(
  numberWidth: number,
  decimalsDigits: number,
  noTrailingZeros = false,
) {
  return ({ getValue }) => {
    const value = getValue();
    let txt = '-';
    if (typeof value === 'string') {
      const i = parseFloat(value);
      if (isNaN(i)) txt = value;
      else {
        //txt = i.toLocaleString();
        txt = i.toLocaleString(undefined, {
          minimumFractionDigits: decimalsDigits,
          maximumFractionDigits: decimalsDigits,
        });
      }
    } else if (typeof value === 'number') {
      txt = value.toLocaleString(undefined, {
        minimumFractionDigits: decimalsDigits,
        maximumFractionDigits: decimalsDigits,
      });
    }
    // pad to 10 chars FIGURE SPACE = &numsp; U+2007
    //console.log(value, numberWidth, txt.length);
    if (numberWidth > txt.length) txt = '\u2007'.repeat(numberWidth - txt.length) + txt;

    // Χωρίς Μηδενικά στο Τέλος
    if (noTrailingZeros) {
      // i.e. 1.20 -> 1.2  & 1.00 -> 1
      if (txt.slice(-3) === '.00')
        txt = txt.substring(0, txt.length - 3) + '\u2008' + '\u2007' + '\u2007';
      if (txt.slice(-1) === '0') txt = txt.substring(0, txt.length - 1) + '\u2007';
    }

    return <div className="text-center">{txt}</div>;
  };
}

export function CellFloatV8(
  numberWidth: number = 8,
  decimalsDigits: number = 2,
  noTrailingZeros = false,
) {
  return CellFloatCustomV8(numberWidth, decimalsDigits, noTrailingZeros);
}

export const CellDateV8 = ({ getValue }) => (
  <div className="text-center">{getValue() ? format(parseISO(getValue()), 'dd/MM/yyyy') : ''}</div>
);

export const CellDateExpiryV8 = ({ getValue }) => (
  <div
    className="text-center"
    style={new Date(getValue()) < new Date() ? { color: 'red', fontWeight: '500' } : undefined}
  >
    {getValue() ? format(parseISO(getValue()), 'dd/MM/yyyy') : ''}
  </div>
);

export const CellDateTimeV8 = ({ getValue }) => (
  <div className="text-center">
    {getValue() ? format(parseISO(getValue()), 'dd/MM/yyyy HH:mm') : ''}
  </div>
);

/* export function CellDateV8(expireRed = false) {
  return ({ getValue }) => {
    return (
      <div
        className="text-center"
        style={expireRed ? { color: '#6b2020' } : undefined}
      >
        {getValue() ? format(parseISO(getValue()), 'dd/MM/yyyy') : ''}
      </div>
    );
  };
}

export function CellDateTimeV8(expireRed = false) {
  return ({ getValue }) => {
    return (
      <div
        className="text-center"
        style={expireRed ? { color: '#6b2020' } : undefined}
      >
        {getValue() ? format(parseISO(getValue()), 'dd/MM/yyyy HH:mm') : ''}
      </div>
    );
  };
} */
